import React, { useState, useEffect } from 'react'
import { OmDialog, OmInput, OmCheckbox, OmSpinner } from '@owma/kit'
import { useTranslation } from 'react-i18next'
import { showResult } from 'utils/ui'

import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import categoryIcon from 'assets/images/contacts/Editor/editor-category-20-color.svg'
import editIcon from 'assets/images/toolbar-edit.svg'
import deleteIcon from 'assets/images/toolbar-delete.svg'
import SVG from 'react-inlinesvg'

import CategoryEditor from '../CategoryEditor'
import styles from './style.module.scss'

interface Category {
  id: number
  name: string
  color: string
}

interface CategorySelectorProps {
  onClose: () => void
  onSave: (categories: Category[]) => void
  onDeletedCategory: (category: Category) => void
  initialData: Category[]
  manageOnly?: boolean
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  onClose,
  onSave,
  onDeletedCategory,
  initialData,
  manageOnly = false,
}) => {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<Category[]>([])
  const [searchText, setSearchText] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(initialData)
  const [isManageMode, setIsManageMode] = useState(manageOnly ? true : false)
  const [loading, setLoading] = useState(false)
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false)
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null)

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await contactHelper.category()
      if (response?.data?.data) {
        const categories = response.data.data.filter((category) => category.id !== 0)
        setCategories(categories)

        if (selectedCategories.length > 0) {
          const refreshedCategories = []
          selectedCategories.forEach((category) => {
            const findCategory = categories.find((c) => c.id === category.id)
            refreshedCategories.push(findCategory)
          })
          setSelectedCategories(refreshedCategories)
        }
      }
    } catch (error) {
      console.error('loading categories error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  const handleCategorySelect = (category: Category) => {
    if (isManageMode) return
    const isSelected = selectedCategories.some((c) => c.id === category.id)
    if (isSelected) {
      setSelectedCategories(selectedCategories.filter((c) => c.id !== category.id))
    } else {
      setSelectedCategories([...selectedCategories, category])
    }
  }

  const handleSave = () => {
    if (isManageMode) {
      setIsManageMode(false)
      return
    }
    onSave(selectedCategories)
    onClose()
  }

  const handleAddCategory = () => {
    setCategoryToEdit(null)
    setCategoryEditorOpen(true)
  }

  const handleEditCategory = (e: React.MouseEvent, category: Category) => {
    e.stopPropagation()
    setCategoryToEdit(category)
    setCategoryEditorOpen(true)
  }

  const handleDeleteCategory = async (e: React.MouseEvent, category: Category) => {
    e.stopPropagation()
    try {
      await contactHelper.deleteCategory(category.id)
      // 삭제 후 목록 새로고침
      await loadCategories()

      setSelectedCategories(selectedCategories.filter((c) => c.id !== category.id))
      onDeletedCategory(category) // 사용자가 저장을 안하고 취소하는 경우 남아있게 되므로 함수 호출
    } catch (error) {
      console.error('delete category error:', error)
    }
  }

  const handleCategoryEditorSave = async (
    categoryData: any,
    completion: (success: boolean) => void = (_success) => {},
  ) => {
    const categoryName = categoryData.name
    const duplicate = categories.some((category) => {
      return (
        category.name.toLowerCase() == categoryName.toLowerCase() && category.id !== categoryData.id
      )
    })
    if (duplicate) {
      showResult({
        type: 'error',
        message: t('contact.desc.failedCategoryDuplicate'),
      })
      return
    }

    let success = true
    try {
      if (categoryData.id) {
        const result = await contactHelper.updateCategory({
          id: categoryToEdit.id,
          name: categoryData.name,
          color: categoryData.color,
        })
        if (Math.floor(result.data.code / 100) !== 2) {
          showResult({
            type: 'error',
            message: t('contact.desc.failedUpdateCategory'),
          })
          success = false
        }
      } else {
        const result = await contactHelper.createCategory({
          name: categoryData.name,
          color: categoryData.color,
        })
        if (result?.data?.data?.id == null) {
          if (result?.data?.code === 419) {
            showResult({
              type: 'error',
              message: t('contact.desc.failedCategoryDuplicate'),
            })
          } else {
            showResult({
              type: 'error',
              message: t('contact.desc.failedNewCategory'),
            })
          }
          success = false
        }
      }
      // 저장 후 목록 새로고침
      await loadCategories()
    } catch (error) {
      console.error('save category error:', error)
      success = false
    }
    completion(success)
  }

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchText.toLowerCase()),
  )

  const modalFooter = [
    <div key="footer" className={styles.footer}>
      <div className={styles.footerLeft}>
        {!isManageMode && (
          <button className={styles.manageButton} onClick={() => setIsManageMode(!isManageMode)}>
            {t('contact.menu.manageCategory')}
          </button>
        )}
      </div>
      <div className={styles.footerRight}>
        {!isManageMode && (
          <button className={styles.cancelButton} onClick={onClose}>
            {t('common.button.cancel')}
          </button>
        )}
        <button
          className={styles.saveButton}
          onClick={() => {
            if (manageOnly) {
              onClose()
            } else {
              handleSave()
            }
          }}
        >
          {isManageMode ? t('common.button.ok') : t('common.button.select')}
        </button>
      </div>
    </div>,
  ]

  return (
    <>
      <OmDialog
        title={isManageMode ? t('contact.menu.manageCategory') : t('contact.menu.categorize')}
        open={true}
        closable={false}
        footer={modalFooter}
        width={500}
        className={styles.modal}
        centered
      >
        <div className={styles.container}>
          <div className={styles.searchContainer}>
            <OmInput
              placeholder={t('email.placeholder.search')}
              value={searchText}
              onChange={handleSearch}
              className={styles.searchInput}
            />
            <button className={styles.addButton} onClick={handleAddCategory}>
              {t('contact.menu.newCategory')}
            </button>
          </div>
          <div className={styles.categoryList}>
            {loading ? (
              <div className={styles.loadingWrapper}>
                <OmSpinner spinning />
              </div>
            ) : (
              filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className={styles.categoryItem}
                  onClick={() => handleCategorySelect(category)}
                  style={{ cursor: isManageMode ? 'default' : 'pointer' }}
                >
                  <div className={styles.categoryNameWrapper}>
                    <div
                      className={styles.categoryIcon}
                      style={
                        {
                          '--category-color': '#' + category.color,
                        } as React.CSSProperties
                      }
                    >
                      <SVG src={categoryIcon} alt="category" {...({} as any)} />
                    </div>
                    <span className={styles.categoryName}>{category.name}</span>
                  </div>
                  {isManageMode ? (
                    <div className={styles.actionButtons}>
                      <button
                        className={styles.iconButton}
                        onClick={(e) => handleEditCategory(e, category)}
                      >
                        <SVG src={editIcon} alt="edit" {...({} as any)} />
                      </button>
                      <button
                        className={styles.iconButton}
                        onClick={(e) => handleDeleteCategory(e, category)}
                      >
                        <SVG src={deleteIcon} alt="delete" {...({} as any)} />
                      </button>
                    </div>
                  ) : (
                    <OmCheckbox
                      checked={selectedCategories.some((c) => c.id === category.id)}
                      onChange={() => {}}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </OmDialog>

      {categoryEditorOpen && (
        <CategoryEditor
          onClose={() => setCategoryEditorOpen(false)}
          onSave={handleCategoryEditorSave}
          initialData={categoryToEdit}
        />
      )}
    </>
  )
}

export default CategorySelector
